import { withStyles, WithStyles, Typography, Theme } from '@material-ui/core'
import i18n from 'i18next'
import { Fragment, FC, useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import {
  CreateApartment,
  Navigate,
  RemoveApartment
} from '../../../actions/types'
import Container from '../../../components/Container'
import GridContainer from '../../../components/Grid/GridContainer'
import { selectHasApartmentsWithoutClientInfo } from '../../../selectors/apartments'
import {
  selectAreUsersLoading,
  selectUserAttributes,
  selectUserId
} from '../../../selectors/users'
import { UserAttributes } from '../../../services/auth/types'
import { Apartment } from '../../../types/apartments'
import { StateInterface } from '../../../types/states'
import AddMissingClientInfoDialog from './AddMissingClientInfoDialog'
import ApartmentCard from './ApartmentCard'
import UserApartmentsTabs from './UserApartmentsTabs'
import UserApartmentsToolbar from './UserApartmentsToolbar'

interface MappedStateProps {
  areUsersLoading: boolean
  userAttributes: UserAttributes | undefined
  hasApartmentsWithoutClientInfo: boolean
}

const mapStateToProps = (state: StateInterface): MappedStateProps => {
  const stateUserAttributes = selectUserAttributes(state)
  const userAttributesNotAnEmptyObject =
    Object.keys(stateUserAttributes || {}).length > 0
  const currentUserId = selectUserId(state)
  return {
    areUsersLoading: selectAreUsersLoading(state),
    userAttributes: userAttributesNotAnEmptyObject
      ? (stateUserAttributes as UserAttributes)
      : undefined,
    hasApartmentsWithoutClientInfo: currentUserId
      ? selectHasApartmentsWithoutClientInfo(currentUserId)(state)
      : false
  }
}

interface OwnProps {
  currentTab?: any
  removeApartment: RemoveApartment
  createApartment: CreateApartment
  navigate: Navigate
  t: i18n.TFunction
  apartments: Apartment[]
  isMobile: boolean
}

type UserApartmentsProps = OwnProps & MappedStateProps & WithStyles

const styles = ({ breakpoints }: Theme) => ({
  apartments: {
    margin: '10px 5px 10px 5px',
    justifyContent: 'center',
    alignItems: 'center',
    [breakpoints.only('xs')]: {
      margin: '0',
      '& .MuiCard-root': {
        margin: '0 0 15px'
      }
    }
  },
  apartmentsTitle: {
    width: '100%',
    padding: '0rem 10rem',
    fontSize: '1.5rem' as const,
    fontWeight: 'normal' as const,
    margin: '0.35rem auto',
    [breakpoints.down('md')]: {
      padding: '0'
    }
  },
  tabsContainer: {
    margin: '16px 0',
    padding: '0rem 10rem',
    [breakpoints.down('md')]: {
      padding: '0'
    }
  }
})

const UserApartmentsGrid = ({
  classes,
  isMobile,
  apartments,
  t,
  removeApartment,
  navigate,
  areUsersLoading,
  userAttributes,
  hasApartmentsWithoutClientInfo,
  currentTab
}: UserApartmentsProps) => {
  return (
    <GridContainer isMobile={isMobile} className={classes.apartments}>
      {apartments.length > 0 && (
        <Fragment>
          {apartments.map((apartment) => {
            if (
              (currentTab === 'draft' &&
                (apartment?.status === 'created' ||
                  apartment?.status === 'confirmed')) ||
              (currentTab === 'rented' &&
                (apartment?.status === 'rented' ||
                  apartment?.status === 'cancelled')) ||
              (currentTab === 'marketing' &&
                (apartment?.status === 'marketing' ||
                  apartment?.status === 'rentContractWanted'))
            )
              return (
                <ApartmentCard
                  key={apartment.apartmentId}
                  apartmentId={apartment.apartmentId}
                  apartment={apartment}
                  removeApartment={removeApartment}
                  navigate={navigate}
                  t={t}
                />
              )
            else return null
          })}
        </Fragment>
      )}
      {hasApartmentsWithoutClientInfo && (
        <AddMissingClientInfoDialog
          {...{
            incompleteApartmentData: apartments.filter(
              (d) => d.status === 'confirmed'
            )[0],
            areUsersLoading,
            userAttributes,
            hasApartmentsWithoutClientInfo
          }}
        />
      )}
    </GridContainer>
  )
}

const UserApartments: FC<UserApartmentsProps> = (props) => {
  const { classes, t, createApartment, userAttributes, apartments } = props
  const [currentTab, setCurrentTab] = useState<
    'draft' | 'marketing' | 'rented'
  >('draft')
  const tabInitializedRef = useRef(false)
  const tabsProps = {
    setCurrentTab,
    currentTab,
    t,
    apartments
  }
  const gridProps = {
    ...props,
    currentTab
  }
  useEffect(() => {
    if (tabInitializedRef.current || apartments.length === 0) return
    if (apartments.some((apartment) => apartment?.status === 'marketing')) {
      setCurrentTab('marketing')
    }
    tabInitializedRef.current = true
  }, [apartments])

  return (
    <Container className={classes.profileContainer}>
      <Typography variant='h5' gutterBottom className={classes.apartmentsTitle}>
        {userAttributes?.name && userAttributes.name}
      </Typography>
      <Typography
        variant='h5'
        gutterBottom
        className={classes.apartmentsTitle}
        style={{ fontSize: '1.3rem' }}
      >
        {`${t('apartments.total')} (${
          props.apartments.filter((apartment) => {
            if (currentTab === 'rented')
              return (
                apartment?.status === 'rented' ||
                apartment?.status === 'cancelled'
              )
            if (currentTab === 'draft')
              return (
                apartment?.status === 'created' ||
                apartment?.status === 'confirmed'
              )
            if (currentTab === 'marketing')
              return (
                apartment?.status === 'marketing' ||
                apartment?.status === 'rentContractWanted'
              )
            return null
          }).length
        })`}
      </Typography>
      <Container className={classes.tabsContainer}>
        <UserApartmentsTabs {...tabsProps} />
      </Container>

      <UserApartmentsGrid {...gridProps} />
      <UserApartmentsToolbar t={t} createApartment={createApartment} />
    </Container>
  )
}

export default connect(mapStateToProps)(withStyles(styles)(UserApartments))
